import type { IntegrationConnection } from "@common/api/brandGroupService";
import type { WorkspaceIntegration } from "@/contexts/AuthContext/useWorkspaceIntegrations";
import { resolveShopifyConnectionDomain } from "@/components/Onboarding/storeUrlNormalization";

const normalizeProvider = (provider: unknown) =>
  String(provider || "").trim().toLowerCase();

const getMappingConnectionIds = (mapping: Record<string, unknown>) =>
  [
    mapping.id,
    mapping.integrationId,
    mapping.workspace_integration_id,
    mapping.workspaceIntegrationId,
    // The current Workspace integrations contract uses connection_id for a
    // store mapping. Without it, Brand-group data falls back to the provider
    // name and the invite modal renders every store as "shopify".
    mapping.connection_id,
    mapping.connectionId,
    mapping.external_connection_id,
    mapping.externalConnectionId,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

/**
 * The Project resource contract intentionally carries a compact connection
 * shape. Hydrate only Shopify's display name from the richer Workspace
 * integration mapping so Business groups show the same store domain as the
 * Integrations page.
 */
export const hydrateShopifyConnectionDomains = (
  contractConnections: IntegrationConnection[],
  workspaceIntegrations: WorkspaceIntegration[],
): IntegrationConnection[] => {
  const domainByConnectionId = new Map<string, string>();

  for (const integration of workspaceIntegrations) {
    if (normalizeProvider(integration.provider) !== "shopify") continue;

    for (const rawMapping of integration.mappings || []) {
      const mapping = rawMapping as unknown as Record<string, unknown>;
      const domain = resolveShopifyConnectionDomain({
        integration,
        mapping,
      });
      if (!domain) continue;

      for (const connectionId of getMappingConnectionIds(mapping)) {
        if (!domainByConnectionId.has(connectionId)) {
          domainByConnectionId.set(connectionId, domain);
        }
      }
    }
  }

  if (domainByConnectionId.size === 0) return contractConnections;

  return contractConnections.map((connection) => {
    if (normalizeProvider(connection.provider) !== "shopify") return connection;

    const domain = domainByConnectionId.get(String(connection.connectionId));
    return domain ? { ...connection, externalAccountName: domain } : connection;
  });
};
